<template>
	<!-- For input type: password -->
	<!-- For Dev: All logic is testing purposes only" -->
	<div
		:class="[
			'input-password',
			...variantClassList,
			disabled && 'is-disabled',
			hasError && 'has-error',
			disabled && 'is-disabled'
		]"
		:disabled="disabled"
	>
		<span class="input-password__label" v-if="labelText">
			<Label :labelText="labelText" :forElementId="uid">
				<template v-slot:leading>
					<slot name="beforeLabel"> </slot>
				</template>

				<template v-slot:trailing>
					<slot name="afterLabel"> </slot>
				</template>
			</Label>
			<span
				:class="'input-password__label-extra'"
				slot="label-extra"
				v-if="$slots['label-extra'] != undefined"
			>
				<slot name="label-extra" />
			</span>
		</span>
		<div
			class="input-password__body"
			:style="inputStyle"
			@focusout="handleBlur"
			ref="inputBody"
			tabindex="0"
		>
			<input
				:id="uid"
				ref="input"
				class="input-password__input"
				:name="name"
				type="password"
				:aria-label="ariaLabel"
				v-model="currentValue"
				:placeholder="placeholder"
				v-focus="hasFocus"
				@input="inputSearchText"
				@change="updateSearchText"
				@keyup="onKeyUp"
				:tabindex="tabindex"
				:autocomplete="autocomplete" 
			/>
			<span
				tabindex="0"
				class="input-password__show-hide"
				:class="{ 'is-disabled': disabledShowHideButton }"
				@click="!disabledShowHideButton && doShowHide()"
				ref="showHideSpan"
				:disabled="disabledShowHideButton"
			>
				<i
					id="show-hide"
					ref="show-hide"
					class=" icon icon-show"
					
				></i>
			</span>
		</div>

		<HelpFeedback
			class="input-password__help-feedback"
			:style="inputStyle"
			:helpText="helpText"
			v-if="helpText"
		/>
		<ErrorFeedback
			class="input-password__error-feedback"
			:errorMessages="errorMessages"
			v-if="errorMessages.length"
		/>
	</div>
</template>

<script>
import Label from '@/components/forms/shared-stateless-elements/label.vue';
import baseComponentMixin from '@/mixins/base-component-mixin';
import formComponentMixin from '@/mixins/form-component-mixin';
import baseInputMixin from '@/mixins/base-input-mixin';
import HelpFeedback from '@/components/forms/shared-stateless-elements/help-feedback.vue';
import ErrorFeedback from '@/components/forms/shared-stateless-elements/error-feedback.vue';
import variantClasses from './variant-classes/input-password-variant-classes';
import componentVariantMixin from '@/mixins/component-variant-mixin';
import focusMixin from '@/mixins/focus-mixin';

export default {
	name: 'InputPassword',
	mixins: [
		baseComponentMixin,
		formComponentMixin,
		baseInputMixin,
		componentVariantMixin,
		focusMixin
	],
	components: {
		Label,
		HelpFeedback,
		ErrorFeedback
	},
	data() {
		return {
			refField: undefined
		};
	},
	created() {
		this.variantClasses = variantClasses;
	},
	mounted() {
		this.refField = this.$refs.input;
	},
	props: {
		disabledShowHide: {
			type: Boolean,
			default: undefined
		},
		autoHidePassword: {
			type: Boolean,
			default: false
		},
		helpText: {
			type: String,
			default: null
		},
		ariaLabel: {
			type: String,
			default: ''
		},
		width: {
			type: [String, Number],
			default: null
		},
		autocomplete: {
			type: String,
			default: ''
		}
	},
	computed: {
		disabledShowHideButton() {
			return this.disabledShowHide ?? false;
		},
		hasError() {
			return this.errorMessages.length > 0;
		},
		inputStyle() {
			if (typeof this.width === 'number') {
				return {
					width: `${this.width}px`
				};
			} else {
				return {
					width: this.width
				};
			}
		}
	},
	methods: {
		doShowHide() {
			if (!this.disabledShowHideButton) {
				const pass_field = this.$refs['input'];
				const pass_icon = this.$refs['show-hide'];
				if (pass_field.type === 'password') {
					pass_field.type = 'text';
					pass_icon.classList.add('icon-hide');
					pass_icon.classList.remove('icon-show');
				} else {
					pass_field.type = 'password';
					pass_icon.classList.add('icon-show');
					pass_icon.classList.remove('icon-hide');
				}
			}
		},
		handleBlur(event) {
			const pass_field = this.$refs['input'];
			const pass_icon = this.$refs['show-hide'];
			const pass_span = this.$refs.showHideSpan;
			if (this.autoHidePassword && pass_field.type === 'text') {
				pass_field.type = 'password';
				pass_icon.classList.add('icon-show');
				pass_icon.classList.remove('icon-hide');
			}
			if (
				event.relatedTarget !== pass_field &&
				event.relatedTarget !== pass_span
			) {
				this.$emit('passwordBodyBlur', pass_field.type);
			} 
		},
		triggerValidation() {
			this.validate(this.currentValue, undefined);
		}
	}
};
</script>

<style lang="scss" scoped>
@keyframes onAutoFillStart {
	from {
		/**/
	}
	to {
		/**/
	}
}

.input-password {
	font-size: $field-font-size;

	&__label {
		margin-bottom: $gutter-sm;
		display: flex;
		align-items: center;
		gap: $gutter-sm;

		&-extra {
			display: inline-flex;
			align-items: center;
		}
	}

	&__show-hide {
		position: relative;
		border: $field-border;
		padding: $field-padding-y $field-padding-x;
		border-radius: 0 $border-radius $border-radius 0;
		background-color: var(--field-bg);
		color: var(--field-fg);
		display: flex;
		align-items: center;
		cursor: pointer;

		&:focus,
		&:active {
			outline: none;
			&::after {
				content: '';
				@include transform-vertical-center;
				left: 0;
				width: calc(100% + 1px);
				height: calc(100% + 1px);
				border-radius: 0 $border-radius $border-radius 0;
				border: $focus-width $focus-style var(--brand-2);
				pointer-events: none;
			}
		}
	}

	&__body {
		display: flex;
		position: relative;

		&::after {
			content: none;
			@include transform-vertical-center;
			left: 0;
			transform: translateY(-50%);
			width: calc(100% + 1px);
			height: calc(100% + 1px);
			border-radius: $border-radius;
			border: $focus-width $focus-style var(--brand-2);
			pointer-events: none;
		}

		&:focus-within,
		&:active {
			&::after {
				content: '';
				@include transform-vertical-center;
				left: 0;
				width: calc(100% + 1px);
				height: calc(100% + 1px);
				border-radius: $border-radius;
				border: $focus-width $focus-style var(--brand-2);
				pointer-events: none;
			}
		}
	}

	&__input {
		width: 100%;
		border: $field-border;
		border-radius: $border-radius 0 0 $border-radius;
		padding: $field-padding-y $field-padding-x;
		background: var(--field-bg);
		color: var(--field-fg);
		@include input-height;
		@include ellipsis;
		flex-grow: 2;
		position: relative;

		&:focus,
		&:active {
			outline: none;
		}
	}

	.input-password__help-feedback,
	.input-password__error-feedback {
		margin-top: 4px;
	}

	&:deep(input)::-ms-clear,
	&:deep(input)::-ms-reveal {
		display: none;
	}

	&:deep(input) {
		border-right: none;
	}

	& .input-password__body:focus-within::after {
		left: 0;
	}

	&.has-error {
		.input-password__body::after {
			content: '';
			border-color: var(--ui-negative);
		}
	}
}

@include left-label('input-password');
</style>
